import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Zap, ChevronDown, ChevronUp, RefreshCw, X,
  Grid, FileText, PenLine, Loader, Clock, History,
  Send, CheckCircle, AlertCircle, Search,
} from "lucide-react";
import { useOrgId } from "../hooks/useOrgId";
import * as api from "../lib/api";
import { useCmsConnected } from "../components/CmsGate";

const PRIMARY = "#018D4C";

const CLUSTER_COLORS = [
  "#018D4C", "#FF2A00", "#FF8C00", "#9B59B6", "#1ABC9C",
  "#E67E22", "#3498DB", "#E74C3C", "#2ECC71", "#8E44AD",
  "#16A085", "#D35400", "#2980B9", "#27AE60", "#0055FF",
];

// ── Badge color maps ──────────────────────────────────────────────────────────

const TYPE_COLORS = {
  reformulation:    { bg: "#EFF6FF", text: "#1D4ED8", border: "#BFDBFE" },
  related:          { bg: "#F5F3FF", text: "#6D28D9", border: "#DDD6FE" },
  implicit:         { bg: "#FFFBEB", text: "#B45309", border: "#FDE68A" },
  comparative:      { bg: "#F0F9FF", text: "#0369A1", border: "#BAE6FD" },
  entity_expansion: { bg: "#ECFDF5", text: "#065F46", border: "#A7F3D0" },
  personalized:     { bg: "#FFF1F2", text: "#BE123C", border: "#FECDD3" },
};

const INTENT_COLORS = {
  informational: { bg: "#EFF6FF", text: "#1D4ED8" },
  commercial:    { bg: "#FFFBEB", text: "#B45309" },
  transactional: { bg: "#ECFDF5", text: "#065F46" },
  comparison:    { bg: "#F5F3FF", text: "#6D28D9" },
  problem_aware: { bg: "#FFF1F2", text: "#BE123C" },
};

const ALL_TYPES = [
  "reformulation", "related", "implicit",
  "comparative", "entity_expansion", "personalized",
];
const ALL_INTENTS = [
  "informational", "commercial", "transactional", "comparison", "problem_aware",
];

// routing_format → human label + write CTA label
const FORMAT_META = {
  web_article:       { label: "Web Article",       cta: "Write Article" },
  faq_page:          { label: "FAQ Page",           cta: "Write FAQ" },
  comparison_table:  { label: "Comparison Table",   cta: "Write Comparison" },
  buyers_guide:      { label: "Buyer's Guide",      cta: "Write Guide" },
  how_to_steps:      { label: "How-To Guide",       cta: "Write How-To" },
  landing_page:      { label: "Landing Page",       cta: "Write Page" },
  pillar_page:       { label: "Pillar Page",        cta: "Write Pillar" },
  glossary_entry:    { label: "Glossary Entry",     cta: "Write Entry" },
  case_study:        { label: "Case Study",         cta: "Write Case Study" },
  calculator_tool:   { label: "Calculator Tool",    cta: "Write Spec" },
};

// ── Small components ──────────────────────────────────────────────────────────

function TypeBadge({ type }) {
  const c = TYPE_COLORS[type] || { bg: "#F4F4F5", text: "#52525B", border: "#E4E4E7" };
  return (
    <span
      className="px-2 py-0.5 text-[9px] tracking-[0.12em] font-bold uppercase rounded-full border"
      style={{ background: c.bg, color: c.text, borderColor: c.border }}
    >
      {type?.replace(/_/g, " ")}
    </span>
  );
}

function IntentBadge({ intent, confidence }) {
  const c = INTENT_COLORS[intent] || { bg: "#F4F4F5", text: "#52525B" };
  const pct = confidence != null ? Math.round(confidence * 100) : null;
  return (
    <span
      className="px-2 py-0.5 text-[9px] tracking-[0.12em] font-bold uppercase rounded-full"
      style={{ background: c.bg, color: c.text }}
    >
      {intent?.replace(/_/g, " ")}{pct != null ? ` · ${pct}%` : ""}
    </span>
  );
}

function RoutingChip({ format }) {
  if (!format) return null;
  return (
    <span className="px-2 py-0.5 text-[9px] tracking-[0.1em] font-semibold rounded bg-zinc-100 text-zinc-500 border border-zinc-200">
      {format.replace(/_/g, " ")}
    </span>
  );
}

function ClusterChip({ clusterId, clusterLabel, onClick }) {
  if (!clusterId) return null;
  const inner = (
    <span
      className="px-2 py-0.5 text-[9px] tracking-[0.1em] font-bold rounded border"
      style={{ background: "#ECFDF5", color: PRIMARY, borderColor: "#A7F3D0" }}
      title={clusterLabel || clusterId}
    >
      ✓ {clusterLabel ? clusterLabel.slice(0, 20) : "cluster matched"}
    </span>
  );
  if (!onClick) return inner;
  return (
    <button
      onClick={onClick}
      className="hover:opacity-70 transition-opacity"
      title="Check coverage in Clusters"
    >
      {inner}
    </button>
  );
}

function FilterPill({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className="px-3 py-1 text-[10px] tracking-[0.1em] uppercase font-bold rounded-full border transition-all"
      style={
        active
          ? { background: PRIMARY, color: "#fff", borderColor: PRIMARY }
          : { background: "#fff", color: "#71717A", borderColor: "#E4E4E7" }
      }
    >
      {label}
    </button>
  );
}

// ── Approve (outline + WP push, runs server-side in the background) ──────────
//
// The actual work (outline generation + CMS push) runs as a background task on
// the server, keyed off the query row's `approval_status` column — not an
// in-browser await chain — so it survives a page reload or the tab closing.
// This component only starts the job and polls for it. See
// [[backend/routers/fanout.py#_run_approve_gap_query]].

async function pollApproval(queryId, onTick) {
  const MAX_POLLS = 90; // 90 * 2s = 3min
  for (let i = 0; i < MAX_POLLS; i++) {
    await new Promise(r => setTimeout(r, 2000));
    const status = await api.getGapQueryApprovalStatus(queryId);
    onTick(status);
    if (status.approval_status === "done") return status;
    if (status.approval_status === "error") {
      throw new Error(status.approval_error || "Approval failed");
    }
  }
  throw new Error("Approval timed out after 3 minutes — it may still finish in the background, refresh to check.");
}

function ApproveButton({ query, size = "sm" }) {
  const initialStatus = query.approval_status || "idle";
  const [state, setState] = useState(initialStatus);
  const [wpLink, setWpLink] = useState(query.approval_wp_link || null);
  const [err, setErr] = useState(query.approval_error || null);

  const iconSize = size === "sm" ? 9 : 10;
  const textSize = size === "sm" ? "text-[9px]" : "text-[10px]";

  const watch = useCallback((queryId) => {
    pollApproval(queryId, (status) => {
      setState(status.approval_status);
      if (status.approval_wp_link) setWpLink(status.approval_wp_link);
    }).catch((e) => {
      setErr(e.message || "Failed");
      setState("error");
    });
  }, []);

  // Job already running server-side (from an earlier click, or before a page
  // reload) — resume polling instead of showing a stale "Approve" button.
  useEffect(() => {
    if (initialStatus === "generating" || initialStatus === "pushing") {
      watch(query.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const approve = async () => {
    setState("generating");
    setErr(null);
    try {
      await api.approveGapQuery(query.id);
      watch(query.id);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed");
      setState("error");
    }
  };

  if (state === "idle") return (
    <button
      onClick={approve}
      className={`flex items-center gap-1 px-2 py-0.5 ${textSize} font-bold rounded border transition-all shrink-0`}
      style={{ background: "#ECFDF5", color: "#065F46", borderColor: "#A7F3D0" }}
    >
      <Send size={iconSize} />
      Approve
    </button>
  );
  if (state === "generating") return (
    <span className={`flex items-center gap-1 ${textSize} text-zinc-400 font-bold shrink-0`}>
      <Loader size={iconSize} className="animate-spin" />
      Outlining…
    </span>
  );
  if (state === "pushing") return (
    <span className={`flex items-center gap-1 ${textSize} text-zinc-400 font-bold shrink-0`}>
      <Loader size={iconSize} className="animate-spin" />
      Pushing…
    </span>
  );
  if (state === "done") return (
    <span className={`flex items-center gap-1 ${textSize} font-bold text-green-600 shrink-0`}>
      <CheckCircle size={iconSize} />
      {wpLink
        ? <a href={wpLink} target="_blank" rel="noopener noreferrer" className="underline underline-offset-1">Draft live</a>
        : "Draft live"}
    </span>
  );
  if (state === "error") return (
    <button
      onClick={approve}
      title={err}
      className={`flex items-center gap-1 ${textSize} font-bold text-red-500 hover:text-red-700 shrink-0`}
    >
      <AlertCircle size={iconSize} />
      Retry
    </button>
  );
  return null;
}

// ── Content Plan Panel ────────────────────────────────────────────────────────

// Without a CMS there is nowhere to push a draft: gaps go to Ideas (Recommendations),
// which writes outlines that can be copied or downloaded.
function ContentPlanPanel({ gapQueries, cms }) {
  const [open, setOpen] = useState(true);
  if (!gapQueries.length) return null;

  // Group by routing_format
  const groups = {};
  for (const q of gapQueries) {
    const fmt = q.routing_format || "web_article";
    if (!groups[fmt]) groups[fmt] = [];
    groups[fmt].push(q);
  }
  const sortedFormats = Object.keys(groups).sort(
    (a, b) => groups[b].length - groups[a].length
  );

  return (
    <div className="border border-amber-200 bg-amber-50 rounded-xl mb-5 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-amber-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <PenLine size={13} className="text-amber-600" />
          <span className="text-xs font-bold text-amber-800 tracking-wide">
            Content Plan · {gapQueries.length} gaps
          </span>
          <span className="text-[9px] text-amber-600 font-medium">
            {sortedFormats.length} format{sortedFormats.length !== 1 ? "s" : ""}
            {cms === false && " · push gaps to Ideas to write outlines"}
          </span>
        </div>
        {open ? <ChevronUp size={13} className="text-amber-500" /> : <ChevronDown size={13} className="text-amber-500" />}
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-amber-200 space-y-3 mt-3">
          {sortedFormats.map((fmt) => {
            const meta = FORMAT_META[fmt] || { label: fmt.replace(/_/g, " "), cta: "Write" };
            return (
              <div key={fmt}>
                <p className="text-[9px] tracking-[0.2em] uppercase font-bold text-amber-600 mb-1.5">
                  {meta.label} · {groups[fmt].length}
                </p>
                <div className="space-y-1">
                  {groups[fmt].map((q, i) => (
                    <div
                      key={q.id || i}
                      className="flex items-center justify-between gap-3 bg-white border border-amber-100 rounded-lg px-3 py-2"
                    >
                      <p className="text-xs text-zinc-800 flex-1 leading-snug line-clamp-1">{q.query}</p>
                      {cms !== false && <ApproveButton query={q} />}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Query card ────────────────────────────────────────────────────────────────

function QueryCard({ q, index, onClusterClick, cms }) {
  const [open, setOpen] = useState(false);
  const hasDetails = q.reasoning || q.format_reason || q.user_intent;
  const isGap = !q.cluster_id;
  return (
    <div className="bg-white border border-zinc-100 rounded-xl shadow-sm hover:shadow-md transition-shadow">
      <div className="p-4">
        <div className="flex items-start gap-3">
          <span className="text-[10px] font-bold text-zinc-300 mt-0.5 w-5 shrink-0 text-right">
            {index + 1}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-zinc-900 leading-snug mb-2">{q.query}</p>
            <div className="flex flex-wrap gap-1.5 items-center">
              <TypeBadge type={q.type} />
              {q.intent && <IntentBadge intent={q.intent} confidence={q.confidence} />}
              <RoutingChip format={q.routing_format} />
              <ClusterChip
                clusterId={q.cluster_id}
                clusterLabel={q.cluster_label}
                onClick={onClusterClick ? () => onClusterClick(q.cluster_id) : undefined}
              />
              {isGap && cms !== false && <ApproveButton query={q} />}
            </div>
          </div>
          {hasDetails && (
            <button
              onClick={() => setOpen((v) => !v)}
              className="shrink-0 text-zinc-300 hover:text-zinc-500 transition-colors mt-0.5"
            >
              {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          )}
        </div>
        {open && (
          <div className="mt-3 pl-8 space-y-2 border-t border-zinc-50 pt-3">
            {q.user_intent && (
              <div>
                <p className="text-[9px] tracking-[0.15em] uppercase font-bold text-zinc-400 mb-0.5">User intent</p>
                <p className="text-xs text-zinc-600">{q.user_intent}</p>
              </div>
            )}
            {q.reasoning && (
              <div>
                <p className="text-[9px] tracking-[0.15em] uppercase font-bold text-zinc-400 mb-0.5">Why generated</p>
                <p className="text-xs text-zinc-600">{q.reasoning}</p>
              </div>
            )}
            {q.format_reason && (
              <div>
                <p className="text-[9px] tracking-[0.15em] uppercase font-bold text-zinc-400 mb-0.5">Format rationale</p>
                <p className="text-xs text-zinc-600">{q.format_reason}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Seed selector ─────────────────────────────────────────────────────────────

const SEED_TABS = [
  { id: "search",   label: "Search topics",  icon: Search },
  { id: "clusters", label: "Content clusters", icon: Grid },
  { id: "articles", label: "Articles",       icon: FileText },
  { id: "recent",   label: "Recent",         icon: Clock },
  { id: "custom",   label: "Custom",         icon: PenLine },
];

const TIER_COLORS = {
  high:   { bg: "#ECFDF5", text: "#065F46" },
  medium: { bg: "#FFFBEB", text: "#B45309" },
};

function SeedSelector({ onSelect, onLoadHistory, searchTopics, clusters, articles, seeds, loadingSeeds }) {
  // Search topics come from GSC (Gap Analysis), so they exist with or without a CMS.
  const [tab, setTab] = useState("search");
  const [articleSearch, setArticleSearch] = useState("");

  const filteredArticles = useMemo(() => {
    const q = articleSearch.toLowerCase();
    return articles.filter(
      (a) =>
        (a.title || "").toLowerCase().includes(q) ||
        (a.primary_topic || "").toLowerCase().includes(q)
    );
  }, [articles, articleSearch]);

  return (
    <div className="bg-white border border-zinc-200 rounded-2xl shadow-sm overflow-hidden">
      {/* Tabs */}
      <div className="flex border-b border-zinc-100 overflow-x-auto">
        {SEED_TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className="flex items-center gap-2 px-5 py-3 text-xs font-bold tracking-wide uppercase transition-all border-b-2 whitespace-nowrap"
            style={
              tab === id
                ? { color: PRIMARY, borderColor: PRIMARY }
                : { color: "#71717A", borderColor: "transparent" }
            }
          >
            <Icon size={13} />
            {label}
          </button>
        ))}
      </div>

      <div className="p-4">
        {/* Search topics tab */}
        {tab === "search" && (
          loadingSeeds ? (
            <p className="text-xs text-zinc-400 py-2">Loading search topics…</p>
          ) : searchTopics.length === 0 ? (
            <p className="text-xs text-zinc-400 py-2">
              No search topics yet. Run Gap Analysis in Search Performance → Queries to group your GSC queries into topics.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              {searchTopics.map((t) => {
                const tier = TIER_COLORS[t.lead_tier];
                return (
                  <button
                    key={t.id}
                    onClick={() => onSelect(t.topic)}
                    className="flex items-center gap-3 px-3 py-2.5 border border-zinc-100 rounded-xl hover:border-zinc-300 bg-zinc-50 hover:bg-white text-left transition-all group"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-zinc-900 truncate">{t.topic}</p>
                      <p className="text-[9px] text-zinc-400">
                        {t.queries} queries · {(t.impressions || 0).toLocaleString()} impr · {t.coverage_status}
                      </p>
                    </div>
                    {tier && (
                      <span
                        className="px-2 py-0.5 text-[9px] tracking-[0.1em] font-bold uppercase rounded-full shrink-0"
                        style={{ background: tier.bg, color: tier.text }}
                      >
                        {t.lead_tier} lead
                      </span>
                    )}
                    <Zap size={11} className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: PRIMARY }} />
                  </button>
                );
              })}
            </div>
          )
        )}

        {/* Clusters tab */}
        {tab === "clusters" && (
          loadingSeeds ? (
            <p className="text-xs text-zinc-400 py-2">Loading clusters…</p>
          ) : clusters.length === 0 ? (
            <p className="text-xs text-zinc-400 py-2">No content clusters. They come from synced CMS articles; use Search topics instead.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-1 gap-2">
              {clusters.map((c, i) => (
                <button
                  key={c.cluster_id}
                  onClick={() => onSelect(c.label || `Topic ${i + 1}`)}
                  className="flex items-center gap-3 px-3 py-2.5 border border-zinc-100 rounded-xl hover:border-zinc-300 bg-zinc-50 hover:bg-white text-left transition-all group"
                >
                  <div
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ background: CLUSTER_COLORS[i % CLUSTER_COLORS.length] }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-zinc-900 truncate">
                      {c.label || `Topic ${i + 1}`}
                    </p>
                    <p className="text-[9px] text-zinc-400">{c.size} articles</p>
                  </div>
                  <Zap size={11} className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: PRIMARY }} />
                </button>
              ))}
            </div>
          )
        )}

        {/* Articles tab */}
        {tab === "articles" && (
          <>
            <input
              type="text"
              value={articleSearch}
              onChange={(e) => setArticleSearch(e.target.value)}
              placeholder="Search by title or topic…"
              className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-lg mb-3 focus:outline-none"
            />
            {loadingSeeds ? (
              <p className="text-xs text-zinc-400 py-2">Loading articles…</p>
            ) : filteredArticles.length === 0 ? (
              <p className="text-xs text-zinc-400 py-2">No articles match.</p>
            ) : (
              <div className="space-y-1.5">
                {filteredArticles.slice(0, 80).map((a) => {
                  const seed = a.primary_topic || a.title;
                  return (
                    <button
                      key={a.wp_post_id}
                      onClick={() => onSelect(seed)}
                      className="w-full flex items-center justify-between gap-3 px-3 py-2 border border-zinc-100 rounded-lg hover:border-zinc-300 bg-zinc-50 hover:bg-white text-left transition-all group"
                    >
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-zinc-900 truncate">{a.title}</p>
                        {a.primary_topic && a.primary_topic !== a.title && (
                          <p className="text-[9px] text-zinc-400 truncate">topic: {a.primary_topic}</p>
                        )}
                      </div>
                      <Zap size={11} className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: PRIMARY }} />
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Recent tab */}
        {tab === "recent" && (
          loadingSeeds ? (
            <p className="text-xs text-zinc-400 py-2">Loading…</p>
          ) : seeds.length === 0 ? (
            <p className="text-xs text-zinc-400 py-2">No previous fan-outs yet.</p>
          ) : (
            <div className="space-y-1.5">
              {seeds.map((seed) => (
                <button
                  key={seed}
                  onClick={() => onLoadHistory(seed)}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2 border border-zinc-100 rounded-lg hover:border-zinc-300 bg-zinc-50 hover:bg-white text-left transition-all group"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <History size={11} className="text-zinc-400 shrink-0" />
                    <p className="text-xs font-medium text-zinc-900 truncate">{seed}</p>
                  </div>
                  <span className="text-[9px] text-zinc-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    Load saved
                  </span>
                </button>
              ))}
            </div>
          )
        )}

        {/* Custom tab */}
        {tab === "custom" && (
          <p className="text-xs text-zinc-400 py-2">
            Type any seed topic in the input above, then click Run Fan-Out.
          </p>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function FanoutPage() {
  const orgId = useOrgId();
  const navigate = useNavigate();
  const cms = useCmsConnected();
  const [searchParams, setSearchParams] = useSearchParams();

  const [seedTopic, setSeedTopic] = useState(() => searchParams.get("seed") || "");
  const [mode, setMode] = useState("simple");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [intentFilter, setIntentFilter] = useState("all");
  const [pushingToIdeas, setPushingToIdeas] = useState(false);
  const [pushDone, setPushDone] = useState(null); // { added, total } | null

  // Seed data from search topics, existing content + past seeds
  const [searchTopics, setSearchTopics] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [articles, setArticles] = useState([]);
  const [seeds, setSeeds] = useState([]);
  const [loadingSeeds, setLoadingSeeds] = useState(true);

  // Build cluster label lookup for enriching matched results
  const clusterLabelMap = useMemo(() => {
    const map = {};
    clusters.forEach((c) => { if (c.cluster_id) map[c.cluster_id] = c.label; });
    return map;
  }, [clusters]);

  useEffect(() => {
    setLoadingSeeds(true);
    Promise.all([
      api.getQueryTopics({ limit: 100 }).then((d) => d.topics || []).catch(() => []),
      api.getClusters().then((d) => d.clusters || []).catch(() => []),
      api.getArticles({ limit: 200 }).then((d) => d.articles || []).catch(() => []),
      api.getFanoutSeeds().then((d) => d.seeds || []).catch(() => []),
    ])
      .then(([tps, cls, arts, sds]) => {
        setSearchTopics(tps);
        setClusters(cls);
        setArticles(arts);
        setSeeds(sds);
      })
      .catch(() => {})
      .finally(() => setLoadingSeeds(false));
  }, [orgId]);

  const enrichQueries = useCallback((queries) =>
    queries.map((q) => ({
      ...q,
      cluster_label: q.cluster_id ? clusterLabelMap[q.cluster_id] : null,
    })),
  [clusterLabelMap]);

  const run = useCallback(async (topic) => {
    const seed = (topic || seedTopic).trim();
    if (!seed) return;
    setSeedTopic(seed);
    setSearchParams({ seed });
    setLoading(true);
    setResult(null);
    setTypeFilter("all");
    setIntentFilter("all");
    setPushDone(null);
    try {
      const data = await api.fanoutExpandAndClassify(seed, mode);
      if (data.expanded_queries) {
        data.expanded_queries = enrichQueries(data.expanded_queries);
      }
      setResult(data);
      // Refresh seeds list to include new seed
      api.getFanoutSeeds().then((d) => setSeeds(d.seeds || [])).catch(() => {});
    } catch (e) {
      alert("Fan-out failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [seedTopic, mode, enrichQueries]);

  const loadHistory = useCallback(async (seed) => {
    setLoading(true);
    setResult(null);
    setSeedTopic(seed);
    setTypeFilter("all");
    setIntentFilter("all");
    setPushDone(null);
    try {
      const data = await api.getFanoutQueries(seed);
      const queries = enrichQueries(data.queries || []);
      setResult({
        seed_topic: seed,
        expanded_queries: queries,
        generation_details: {},
        saved: queries.length,
        from_history: true,
      });
    } catch (e) {
      alert("Failed to load history: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [enrichQueries]);

  // Auto-run when arriving from another page via ?seed= param
  useEffect(() => {
    const seed = searchParams.get("seed");
    if (seed && !loading && !result) {
      // ?history=1 (from the Query Map) opens the saved rows instead of re-running the LLM.
      if (searchParams.get("history")) loadHistory(seed);
      else run(seed);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run once on mount

  const handleSelect = (topic) => {
    setSeedTopic(topic);
    run(topic);
  };

  const handlePushToIdeas = async () => {
    setPushingToIdeas(true);
    try {
      const res = await api.fanoutToRecommendations(seedTopic);
      setPushDone(res);
    } catch (e) {
      alert("Push failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setPushingToIdeas(false);
    }
  };

  const handleClusterClick = () => {
    navigate("/dashboard/search-performance?tab=topics");
  };

  // Derived filtered list
  const queries = result?.expanded_queries || [];
  const gapQueries = queries.filter((q) => !q.cluster_id);
  const filtered = queries.filter((q) => {
    const typeOk = typeFilter === "all" || q.type === typeFilter;
    const intentOk = intentFilter === "all" || q.intent === intentFilter;
    return typeOk && intentOk;
  });

  const typeCounts = {};
  const intentCounts = {};
  queries.forEach((q) => {
    typeCounts[q.type] = (typeCounts[q.type] || 0) + 1;
    if (q.intent) intentCounts[q.intent] = (intentCounts[q.intent] || 0) + 1;
  });

  return (
    <div className="p-6 sm:p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6 pb-6 border-b border-zinc-100">
        <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-1">AI Search Intelligence</p>
        <h1 className="text-4xl font-bold text-zinc-900 tracking-tight">Query Fan-Out</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Simulate how AI search engines expand a topic into sub-queries — pulled from your search topics, clusters &amp; articles
        </p>
      </div>

      {/* Input row */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="flex-1 relative">
          <input
            type="text"
            value={seedTopic}
            onChange={(e) => setSeedTopic(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run()}
            placeholder="Pick a topic below or type your own…"
            className="w-full px-4 py-3 text-sm border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent pr-10"
          />
          {seedTopic && (
            <button
              onClick={() => { setSeedTopic(""); setResult(null); setPushDone(null); setSearchParams({}); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-300 hover:text-zinc-500"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className="flex border border-zinc-200 rounded-xl overflow-hidden shrink-0">
          {["simple", "complex"].map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className="px-4 py-3 text-xs font-bold tracking-wide uppercase transition-all"
              style={
                mode === m
                  ? { background: PRIMARY, color: "#fff" }
                  : { background: "#fff", color: "#71717A" }
              }
            >
              {m === "simple" ? "Simple (10+)" : "Complex (20+)"}
            </button>
          ))}
        </div>

        <button
          onClick={() => run()}
          disabled={loading || !seedTopic.trim()}
          className="flex items-center gap-2 px-6 py-3 text-sm font-bold tracking-wide rounded-xl transition-all shrink-0 disabled:opacity-40 disabled:cursor-not-allowed text-white hover:opacity-90"
          style={{ backgroundColor: PRIMARY }}
        >
          <Zap size={14} strokeWidth={2.5} className={loading ? "animate-pulse" : ""} />
          {loading ? "RUNNING…" : "RUN FAN-OUT"}
        </button>
      </div>

      {/* Seed selector — hide while results showing to save space */}
      {!result && !loading && (
        <div className="mb-8">
          <SeedSelector
            onSelect={handleSelect}
            onLoadHistory={loadHistory}
            searchTopics={searchTopics}
            clusters={clusters}
            articles={articles}
            seeds={seeds}
            loadingSeeds={loadingSeeds}
          />
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="bg-white border border-zinc-100 rounded-2xl shadow-sm p-10 text-center mb-8">
          <RefreshCw size={28} className="mx-auto mb-3 animate-spin" style={{ color: PRIMARY }} />
          <p className="text-sm font-bold text-zinc-900">
            {result === null && seedTopic ? "Loading saved results…" : "Generating queries…"}
          </p>
          <p className="text-xs text-zinc-400 mt-1">
            {seedTopic && <><em>{seedTopic}</em></>}
          </p>
        </div>
      )}

      {/* Results */}
      {result && !loading && (
        <>
          {/* Breadcrumb / change topic / push to ideas */}
          <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-400">Seed topic</span>
              <span
                className="px-3 py-1 text-xs font-bold rounded-full border"
                style={{ background: "#ECFDF5", color: PRIMARY, borderColor: "#A7F3D0" }}
              >
                {result.seed_topic}
              </span>
              {result.from_history && (
                <span className="text-[9px] text-zinc-400 flex items-center gap-1">
                  <History size={9} /> saved · <button onClick={() => run(result.seed_topic)} className="underline hover:text-zinc-700">refresh</button>
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {gapQueries.length > 0 && (
                pushDone ? (
                  <span className="text-[10px] font-bold text-green-700 bg-green-50 border border-green-200 px-3 py-1 rounded-full">
                    ✓ {pushDone.added} gaps added to Ideas
                  </span>
                ) : (
                  <button
                    onClick={handlePushToIdeas}
                    disabled={pushingToIdeas}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold rounded-full border transition-all disabled:opacity-40"
                    style={{ background: "#FFFBEB", color: "#B45309", borderColor: "#FDE68A" }}
                  >
                    {pushingToIdeas ? <Loader size={10} className="animate-spin" /> : <Zap size={10} />}
                    {pushingToIdeas ? "Pushing…" : `Push ${gapQueries.length} gaps to Ideas`}
                  </button>
                )
              )}
              <button
                onClick={() => { setResult(null); setSeedTopic(""); setPushDone(null); setSearchParams({}); }}
                className="text-xs font-bold transition-colors hover:opacity-70"
                style={{ color: PRIMARY }}
              >
                ← Change topic
              </button>
            </div>
          </div>

          {/* LLM reasoning */}
          {result.generation_details?.reasoning_for_count && (
            <div
              className="border rounded-xl p-4 mb-5 text-sm"
              style={{ background: "#ECFDF5", borderColor: "#A7F3D0", color: "#065F46" }}
            >
              <span className="font-bold">LLM reasoning: </span>
              {result.generation_details.reasoning_for_count}
            </div>
          )}

          {/* Content Plan panel — gap queries grouped by format */}
          <ContentPlanPanel gapQueries={gapQueries} cms={cms} />

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[
              { label: "Queries generated", value: queries.length, color: "text-zinc-900" },
              { label: "Cluster matched",   value: queries.filter((q) => q.cluster_id).length, color: null, primary: true },
              { label: "Content gaps",       value: gapQueries.length, color: "text-zinc-900" },
              {
                label: "High-value intent",
                value: queries.filter((q) => q.intent === "commercial" || q.intent === "transactional").length,
                color: "text-zinc-900",
              },
            ].map(({ label, value, primary }) => (
              <div key={label} className="bg-white border border-zinc-100 rounded-xl p-4 text-center shadow-sm">
                <p
                  className="text-3xl font-bold"
                  style={primary ? { color: PRIMARY } : undefined}
                >
                  {value}
                </p>
                <p className="text-[10px] tracking-[0.12em] uppercase text-zinc-400 mt-0.5">{label}</p>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="mb-5 space-y-3">
            <div>
              <p className="text-[9px] tracking-[0.2em] uppercase font-bold text-zinc-400 mb-2">Query type</p>
              <div className="flex flex-wrap gap-2">
                <FilterPill label="All" active={typeFilter === "all"} onClick={() => setTypeFilter("all")} />
                {ALL_TYPES.filter((t) => typeCounts[t]).map((t) => (
                  <FilterPill
                    key={t}
                    label={`${t.replace(/_/g, " ")} (${typeCounts[t]})`}
                    active={typeFilter === t}
                    onClick={() => setTypeFilter(t)}
                  />
                ))}
              </div>
            </div>
            <div>
              <p className="text-[9px] tracking-[0.2em] uppercase font-bold text-zinc-400 mb-2">Intent</p>
              <div className="flex flex-wrap gap-2">
                <FilterPill label="All" active={intentFilter === "all"} onClick={() => setIntentFilter("all")} />
                {ALL_INTENTS.filter((i) => intentCounts[i]).map((i) => (
                  <FilterPill
                    key={i}
                    label={`${i.replace(/_/g, " ")} (${intentCounts[i]})`}
                    active={intentFilter === i}
                    onClick={() => setIntentFilter(i)}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-zinc-500">
              Showing {filtered.length} of {queries.length} queries
              {result.saved > 0 && ` · ${result.saved} saved`}
            </p>
          </div>

          {filtered.length === 0 ? (
            <div className="bg-white border border-zinc-100 rounded-xl p-8 text-center text-sm text-zinc-400">
              No queries match current filters.
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((q, i) => (
                <QueryCard
                  key={q.id || i}
                  q={q}
                  index={i}
                  onClusterClick={q.cluster_id ? handleClusterClick : undefined}
                  cms={cms}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Empty state */}
      {!result && !loading && searchTopics.length === 0 && clusters.length === 0 && articles.length === 0 && !loadingSeeds && (
        <div className="bg-white border border-zinc-100 rounded-2xl p-14 text-center shadow-sm mt-4">
          <Zap size={40} className="mx-auto mb-4" style={{ color: "#D4D4D8" }} />
          <p className="text-sm font-bold text-zinc-900 mb-1">No data yet</p>
          <p className="text-xs text-zinc-400">
            Run Gap Analysis in Search Performance — your search topics will appear here as seed options.
          </p>
        </div>
      )}
    </div>
  );
}
